import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { BackdropCircular } from 'components';
import {
  getCustomerSubscriptionsApi
} from 'api';
import { useStyles } from './styles';
import CustomTableToolbar from './CustomTableToolbar';
import FilterDrawer from './FilterDrawer';
import CustomerSubscriptionsRow from './CustomerSubscriptionsRow';


const CustomerSubscriptionsTable = ({customerId}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState({});
  const [searchConditions, setSearchConditions] = useState({});

  const {
    customerSubscriptions: {
      items,
      totalCount
    },
    loadingCustomerSubscriptions
  } = useSelector((state: IReduxState) => state.customers);

  useEffect(() => {
    getCustomerSubscriptionsApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: [
        {
          field: 'customer_id',
          value: customerId,
          conditionType: 'eq'
        },
        ...Object.values(filterConditions)
      ],
      searchConditions
    });
  }, [
    sortDirection,
    sortField,
    rowsPerPage,
    currentPage,
    customerId,
    searchConditions,
    filterConditions
  ]);

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  return (
    <>
      <div className={classes.toolbarContainer}>
        <CustomTableToolbar
          onSearch={onSearch}
          setOpenFilter={setOpenFilter}
          filterConditions={filterConditions}
        />
      </div>
      <div className="table-container">
        <TableContainer>
          <Table aria-label="collapsible table" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'increment_id'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'increment_id')}
                  >
                    <div className="px-5">
                      {'ID'}
                    </div>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'description'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'description')}
                  >
                    {'Description'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'status'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'status')}
                  >
                    {'Subscription Status'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'created_at'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'created_at')}
                  >
                    {'Purchased (Date)'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'last_run'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'last_run')}
                  >
                    {'Last Run (Date)'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'next_run'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'next_run')}
                  >
                    {'Next Run (Date)'}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'subtotal'}
                    IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                    onClick={(event) => handleRequestSort(event, 'subtotal')}
                  >
                    {'Subtotal'}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!items.length ?
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No matching subscriptions found.
                  </TableCell>
                </TableRow>
                :
                <>
                  {items.map((subscription) => (
                    <CustomerSubscriptionsRow
                      key={subscription.transaction_id}
                      subscription={subscription}
                    />
                  ))}
                </>
              }
            </TableBody>
          </Table>
        </TableContainer>
        {!!totalCount &&
        <TablePagination
          rowsPerPageOptions={[10, 20, 40, 80, 100]}
          component="div"
          className={classes.paginationContainer}
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={currentPage - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        }
        <BackdropCircular loading={loadingCustomerSubscriptions} />
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <FilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
    </>
  );
};

export default CustomerSubscriptionsTable;
